/**
 * FloatingHopeButton - Always-accessible AI companion
 *
 * A floating button that appears on all pages within the app.
 * Opens HopeModal as an overlay when clicked (stays on current page).
 * Passes current page context for context-aware support.
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import HopeModal from './HopeModal';
import { HopeVoiceService } from '../services/HopeVoiceService';

interface FloatingHopeButtonProps {
  patientId: string;
  patientName?: string;
}

export default function FloatingHopeButton({ patientId, patientName }: FloatingHopeButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const location = useLocation();

  // Auto-initialize Hope voice if previously enabled
  useEffect(() => {
    const settings = HopeVoiceService.getSettings();
    if (settings.enabled) {
      HopeVoiceService.enable();
    }
    return () => {
      // Don't disable on unmount — let it persist across page navigation
    };
  }, []);

  // Keyboard shortcut: Alt+H to toggle Hope chat, Alt+R to toggle Hope Reads Aloud
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === 'h') {
        e.preventDefault();
        setIsModalOpen(prev => !prev);
        setHasNewMessage(false);
      }
      if (e.altKey && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        HopeVoiceService.toggle();
      }
      // Space bar stops Hope from reading (when not in an input)
      if (e.key === ' ' && HopeVoiceService.isSpeaking()) {
        const target = e.target as HTMLElement;
        const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
                        target.contentEditable === 'true';
        if (!isInput) {
          e.preventDefault();
          HopeVoiceService.stop();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Listen for custom open-hope-ai event (from other components)
  useEffect(() => {
    const handleOpenHopeAI = () => {
      setIsModalOpen(true);
      setHasNewMessage(false);
    };
    window.addEventListener('open-hope-ai', handleOpenHopeAI);
    return () => window.removeEventListener('open-hope-ai', handleOpenHopeAI);
  }, []);

  // Get context-aware tooltip based on current page
  const getContextualHint = (): string => {
    const path = location.pathname;
    if (path.includes('appointment')) return 'Need help with appointments?';
    if (path.includes('prescription') || path.includes('medication')) return 'Questions about your medications?';
    if (path.includes('forms')) return 'Questions about the forms?';
    if (path.includes('resources')) return 'Looking for coping strategies?';
    if (path.includes('homework') || path.includes('assignments')) return 'Need help with homework?';
    if (path.includes('measures')) return 'Questions about your assessments?';
    if (path.includes('treatment-plan')) return 'Questions about your treatment plan?';
    if (path.includes('billing') || path.includes('payment')) return 'Billing questions?';
    if (path.includes('video') || path.includes('session')) return 'Preparing for your session?';
    if (path.includes('dashboard')) return 'How can I support you today?';
    return 'Chat with Hope';
  };

  const handleToggle = () => {
    setIsModalOpen(true);
    setHasNewMessage(false);
  };

  const [isHovered, setIsHovered] = useState(false);

  return (
    <>
      {/* Floating Button */}
      <div
        style={styles.floatingButton}
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="button"
        tabIndex={0}
        aria-label={`Open Hope AI assistant. ${getContextualHint()} Press Alt+H for keyboard shortcut.`}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(); } }}
      >
        <div style={styles.hopeIcon} aria-hidden="true">💙</div>
        <div style={styles.askHopeText}>Ask Hope</div>
        {hasNewMessage && <div style={styles.notificationBadge} aria-label="New message">1</div>}
        {isHovered && <div style={styles.hoverText}>{getContextualHint()}</div>}
      </div>

      {/* Hope Modal Overlay */}
      <HopeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        patientId={patientId}
        patientName={patientName}
      />
    </>
  );
}

// Styles
const styles: { [key: string]: React.CSSProperties } = {
  floatingButton: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    minWidth: '140px',
    height: '60px',
    backgroundColor: '#87CEEB',
    borderRadius: '30px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    zIndex: 1000,
    padding: '8px 16px',
    gap: '2px',
  },
  hopeIcon: {
    fontSize: '24px',
    lineHeight: '1',
  },
  askHopeText: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#0A2540',
    lineHeight: '1',
    marginTop: '2px',
  },
  notificationBadge: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    backgroundColor: '#ff6b6b',
    color: '#ffffff',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  hoverText: {
    position: 'absolute',
    right: '150px',
    backgroundColor: '#0A2540',
    color: '#ffffff',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    opacity: 1,
    pointerEvents: 'none',
    transition: 'opacity 0.3s ease',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
  },
};
